import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import useStore, { useData, useSchema } from "../../../hooks/useStore";
import { motion, useDragControls, useMotionValue } from "framer-motion";
import { cn } from "../../../utils";
import {
  AlignJustify,
  ChevronRight,
  Crosshair,
  ExternalLink,
  Minimize2,
} from "lucide-react";
import { getQueryPath, prettierTitle } from "../utils";
import { HostInfoObjectContext, renderData } from "../UiRenderer";
import { ViewContext } from "../View";
import { handleDerived } from "../logics/derived";

export const DbRenderer = ({
  infoObjectName,
  path,
  name,
  data,
  schema,
  options,
}) => {
  const { rendered } = useContext(ViewContext);

  const [unfolded, setUnfolded] = useState(false);
  const [targeted, setTargeted] = useState(false);
  const buttonRef = useRef(null);

  const allData = useData();
  const allSchema = useStore((state) => state.infoObjects);

  const summaryTitle = schema?.summary?.name;

  const derivedValue = useMemo(() => {
    return handleDerived(allData, allSchema, infoObjectName, path, schema);
  }, [allData, allSchema, infoObjectName, path, schema]);

  return (
    <div className="r-db-container">
      <button
        ref={buttonRef}
        className={cn(
          "r-db-button",
          rendered && "r-rendered",
          unfolded && "r-unfolded",
          targeted && "r-targeted"
        )}
      >
        <span className={cn("r-key r-db-key", rendered && "r-rendered")}>
          <span>{prettierTitle(name)}</span>
          <span className="r-db-button-count">{data.length}</span>
        </span>
        <span
          className={cn(
            "r-db-button-content",
            unfolded && "r-unfolded",
            targeted && "r-targeted"
          )}
          onClick={(e) => {
            e.stopPropagation();
            setUnfolded(!unfolded);
          }}
        >
          <div className="r-db-button-summary">
            {summaryTitle && (
              <span className={cn("r-key", rendered && "r-rendered")}>
                {prettierTitle(summaryTitle)}
              </span>
            )}
            <span className="r-db-button-summary-value">{derivedValue}</span>
          </div>
          <ChevronRight
            size={12}
            color="#999"
            style={{
              transform: "translateY(0.5px)", // make visually vertically centered
            }}
          />
        </span>
      </button>

      {unfolded &&
        createPortal(
          <FloatingWindowRenderer
            infoObjectName={infoObjectName}
            path={path}
            name={name}
            data={data}
            setUnfolded={setUnfolded}
            buttonRef={buttonRef}
            setTargeted={setTargeted}
          />,
          document.body.querySelector("#root")
        )}
    </div>
  );
};

export const FloatingWindowRenderer = memo(
  ({
    infoObjectName,
    path,
    name,
    data,
    setUnfolded,
    buttonRef,
    setTargeted,
  }) => {
    const schema = useSchema(infoObjectName, path);

    const addView = useStore((state) => state.addView);

    const width = useMotionValue(400);
    const height = useMotionValue(400);
    const left = useMotionValue(0);
    const top = useMotionValue(0);
    const controls = useDragControls();
    const resizeRef = useRef(null);
    const contentRef = useRef(null);

    const { setTargeted: setHostInfoObjectTarget } = useContext(
      HostInfoObjectContext
    );

    useEffect(() => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        left.set(rect.right + 10);
        top.set(rect.top);
      }
    }, [buttonRef, left, top]);

    useEffect(() => {
      if (contentRef.current) {
        const contentHeight = contentRef.current.scrollHeight;

        if (contentHeight < 400 - 1.75 * 16) {
          height.set(contentHeight + 1.75 * 16);
        }
      }
    }, [height]);

    const handleResizeStart = (e) => {
      e.stopPropagation();
      e.preventDefault();

      const startX = e.clientX;
      const startY = e.clientY;
      const startWidth = width.get();
      const startHeight = height.get();

      const handleResize = (e) => {
        e.stopPropagation();
        e.preventDefault();

        width.set(Math.max(200, startWidth + e.clientX - startX));
        height.set(Math.max(200, startHeight + e.clientY - startY));

        return false;
      };

      const handleResizeEnd = (e) => {
        e.stopPropagation();
        e.preventDefault();

        document.removeEventListener("mousemove", handleResize);
        document.removeEventListener("mouseup", handleResizeEnd);
      };

      document.addEventListener("mousemove", handleResize);
      document.addEventListener("mouseup", handleResizeEnd);
    };

    const scrollToParent = () => {
      const parent = buttonRef.current.closest(".r-db-container");
      parent.scrollIntoView({ behavior: "smooth" });
    };

    const pinView = useCallback(() => {
      const pinnedPath = getQueryPath(path, 1);

      setUnfolded(false);
      setTargeted(false);
      setHostInfoObjectTarget(false);

      addView({
        infoObject: infoObjectName,
        path: pinnedPath,
        viewType: "list", // ?
      });
    }, [
      addView,
      infoObjectName,
      path,
      setHostInfoObjectTarget,
      setTargeted,
      setUnfolded,
    ]);

    return (
      <motion.div
        className="r-db-side"
        style={{
          width,
          height,
          left,
          top,
          position: "fixed",
        }}
        drag
        dragListener={false}
        dragMomentum={false}
        dragControls={controls}
        onMouseEnter={() => {
          setTargeted(true);
          setHostInfoObjectTarget(true);
        }}
        onMouseLeave={() => {
          setTargeted(false);
          setHostInfoObjectTarget(false);
        }}
      >
        <div className="r-db-header" onPointerDown={(e) => controls.start(e)}>
          <div>
            <div
              className="r-icon r-icon-clickable"
              onClick={() => scrollToParent()}
            >
              <Crosshair size={12} />
            </div>
            <div className="r-icon" style={{ color: "#aaa" }}>
              <AlignJustify size={12} />
            </div>
            <span>{prettierTitle(name)}</span>
          </div>
          <div>
            <div className="r-icon r-icon-clickable" onClick={() => pinView()}>
              <ExternalLink size={12} />
            </div>
            <div
              className="r-icon r-icon-clickable r-icon-warning"
              onClick={() => {
                setUnfolded(false);
                setTargeted(false);
                setHostInfoObjectTarget(false);
              }}
            >
              <Minimize2 size={12} />
            </div>
          </div>
        </div>
        <div className="r-db-content" ref={contentRef}>
          {/* <ListRenderer
            infoObjectName={infoObjectName}
            path={path}
            name={name}
            data={data}
            schema={schema}
            options={{
              hideKeys: true,
            }}
            is={{
              view: false,
              floatingWindow: true,
              previewView: false,
            }}
          /> */}
          {renderData(
            infoObjectName,
            path,
            name,
            data,
            schema,
            {
              hideKeys: true,
            },
            {
              view: false,
              floatingWindow: true,
              previewView: false,
            }
          )}
        </div>
        <div
          className="r-db-resize-handle"
          ref={resizeRef}
          onMouseDown={handleResizeStart}
        />
      </motion.div>
    );
  }
);
